import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AUTH_REFRESH_TOKEN, AUTH_TOKEN, AUTH_USER } from 'constants/AuthConstant';
import FirebaseService from 'services/FirebaseService';
import AuthService from 'services/AuthService';

export const initialState = {
	loading: false,
	message: '',
	showMessage: false,
	redirect: '',
	token: localStorage.getItem(AUTH_TOKEN) || null,
	user: JSON.parse(localStorage.getItem(AUTH_USER)) || null,
}

export const signIn = createAsyncThunk('auth/login', async (data, { rejectWithValue }) => {
	const { email, password } = data
	try {
		console.log('Logging in with email:', email);
		const response = await AuthService.login({ email, password })

		// Correctly destructure the response from the backend
		const { accessToken, refreshToken, user } = response.data.data

		if (!accessToken) {
			// Throw an error if the token is not received
			throw new Error('Authentication failed, token not received.');
		}

		console.log('Access Token:', accessToken);

		localStorage.setItem(AUTH_TOKEN, accessToken);
		localStorage.setItem(AUTH_REFRESH_TOKEN, refreshToken);
		localStorage.setItem(AUTH_USER, JSON.stringify(user));
		return { user, token: accessToken };
	} catch (err) {
		const errorMessage = err.response?.data?.message || err.message || 'Error';
		console.error('Login failed:', errorMessage);
		return rejectWithValue(errorMessage);
	}
})

export const signUp = createAsyncThunk('auth/register', async (data, { rejectWithValue }) => {
	const { email, password, username } = data // Add username
	try {
		// Use the correct service for admin registration
		const response = await AuthService.register({ email, password, username })

		// Correctly destructure the response
		const { accessToken, refreshToken, user } = response.data.data

		if (!accessToken) {
			throw new Error('Registration failed, token not received.');
		}

		return { user, token: accessToken };
	} catch (err) {
		const errorMessage = err.response?.data?.message || 'Error';
		return rejectWithValue(errorMessage);
	}
})


export const signOut = createAsyncThunk('auth/logout', async () => {
	localStorage.removeItem(AUTH_TOKEN);
	localStorage.removeItem(AUTH_REFRESH_TOKEN);
	localStorage.removeItem(AUTH_USER);
	return null
})

export const signInWithGoogle = createAsyncThunk('auth/signInWithGoogle', async (_, { rejectWithValue }) => {
	try {
		const response = await AuthService.loginInOAuth()
		const token = response.data.token;
		localStorage.setItem(AUTH_TOKEN, token);
		localStorage.setItem(AUTH_REFRESH_TOKEN, response.data.refreshToken);
		localStorage.setItem(AUTH_USER, JSON.stringify(response.data.user));
		return token;
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || 'Error')
	}
})

export const signInWithFacebook = createAsyncThunk('auth/signInWithFacebook', async (_, { rejectWithValue }) => {
	try {
		const response = await AuthService.loginInOAuth()
		const token = response.data.token;
		localStorage.setItem(AUTH_TOKEN, token);
		localStorage.setItem(AUTH_REFRESH_TOKEN, response.data.refreshToken);
		return token;
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || 'Error')
	}
})


export const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		authenticated: (state, action) => {
			state.loading = false
			state.redirect = '/'
			state.token = action.payload.token
			state.user = action.payload.user
		},
		showAuthMessage: (state, action) => {
			state.message = action.payload
			state.showMessage = true
			state.loading = false
		},
		hideAuthMessage: (state) => {
			state.message = ''
			state.showMessage = false
		},
		signOutSuccess: (state) => {
			state.loading = false
			state.token = null
			state.redirect = '/'
		},
		showLoading: (state) => {
			state.loading = true
		},
		hideLoading: (state) => {
			state.loading = false
		},
		signInSuccess: (state, action) => {
			state.loading = false
			state.token = action.payload
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(signIn.pending, (state) => {
				state.loading = true
			})
			.addCase(signIn.fulfilled, (state, action) => {
				state.loading = false
				state.redirect = '/dashboards/default'
				state.token = action.payload.token
				state.user = action.payload.user
				state.showMessage = false
			})
			.addCase(signIn.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
			.addCase(signOut.fulfilled, (state) => {
				state.loading = false
				state.token = null
				state.redirect = '/'
			})
			.addCase(signOut.rejected, (state) => {
				state.loading = false
				state.token = null
				state.redirect = '/'
			})
			.addCase(signUp.pending, (state) => {
				state.loading = true
			})
			.addCase(signUp.fulfilled, (state, action) => {
				state.loading = false
				state.redirect = '/auth/login'
				state.showMessage = false
			})
			.addCase(signUp.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
			.addCase(signInWithGoogle.pending, (state) => {
				state.loading = true
			})
			.addCase(signInWithGoogle.fulfilled, (state, action) => {
				state.loading = false
				state.redirect = '/'
				state.token = action.payload
			})
			.addCase(signInWithGoogle.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
			.addCase(signInWithFacebook.pending, (state) => {
				state.loading = true
			})
			.addCase(signInWithFacebook.fulfilled, (state, action) => {
				state.loading = false
				state.redirect = '/'
				state.token = action.payload
			})
			.addCase(signInWithFacebook.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
	},
})

export const {
	authenticated,
	showAuthMessage,
	hideAuthMessage,
	signOutSuccess,
	showLoading,
	signInSuccess
} = authSlice.actions

export default authSlice.reducer